import React from 'react';
import Button from '../common/Button';
import { Campaign } from '../../types/campaign.types';
import { formatDate, formatNumber } from '../../utils/formatters';

interface CampaignReceiptProps {
    campaign: Campaign;
}

const formatMoney = (currency: string, amount: number): string => {
    return `${currency} ${Number(amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const CampaignReceipt: React.FC<CampaignReceiptProps> = ({ campaign }) => {
    const currency = campaign.currency || 'GHS';
    const billedMessages = campaign.total_sent || campaign.total_contacts || 0;
    const smsCharge = billedMessages * (campaign.amount_per_sms || 0);
    const totalDue = smsCharge + (campaign.workmanship_fee || 0) + (campaign.sender_id_price || 0);
    const receiptNumber = `HER-${String(campaign.id).padStart(5, '0')}`;
    const clientName = campaign.client_name || 'Client';
    const receiptDate = campaign.completed_at || campaign.started_at || campaign.updated_at || campaign.created_at;

    const emailSubject = encodeURIComponent(`Receipt ${receiptNumber} for ${campaign.name}`);
    const emailBody = encodeURIComponent(
        [
            `Hello ${clientName},`,
            '',
            `Please find your payment receipt for "${campaign.name}".`,
            '',
            `Receipt: ${receiptNumber}`,
            `Campaign: ${campaign.name}`,
            `Messages billed: ${formatNumber(billedMessages)}`,
            `Amount due: ${currency} ${Number(totalDue || 0).toFixed(2)}`,
            '',
            'Thank you.'
        ].join('\n')
    );
    const mailTo = campaign.client_email
        ? `mailto:${campaign.client_email}?subject=${emailSubject}&body=${emailBody}`
        : undefined;

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 mb-5">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Payment Receipt</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Generate a branded receipt after this campaign has been executed.
                    </p>
                </div>
                <div className="flex shrink-0 gap-2 print:hidden">
                    <Button size="sm" variant="outline" onClick={() => window.print()}>
                        Print
                    </Button>
                    <Button
                        size="sm"
                        variant="primary"
                        disabled={!mailTo}
                        onClick={() => {
                            if (mailTo) {
                                window.location.href = mailTo;
                            }
                        }}
                    >
                        Send
                    </Button>
                </div>
            </div>

            {!campaign.client_email && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 print:hidden">
                    Add a client email to this campaign to send the receipt directly from your email app.
                </div>
            )}

            <div id="campaign-receipt" className="overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900">
                <div className="border-b border-gray-200 bg-gray-950 px-6 py-5 text-white">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                            <img src="/logo-brand.png" alt="Hermes" className="h-12 w-12 rounded-lg bg-white object-contain p-1" />
                            <div>
                                <p className="text-sm font-medium text-blue-100">Hermes Bulk SMS</p>
                                <h3 className="text-2xl font-bold tracking-tight">Receipt</h3>
                            </div>
                        </div>
                        <div className="text-left sm:text-right">
                            <p className="text-sm text-blue-100">Receipt No.</p>
                            <p className="font-semibold">{receiptNumber}</p>
                        </div>
                    </div>
                </div>

                <div className="grid gap-6 px-6 py-6 sm:grid-cols-2">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Bill To</p>
                        <p className="mt-2 text-lg font-semibold">{clientName}</p>
                        <p className="text-sm text-gray-600">{campaign.client_email || 'No email on file'}</p>
                    </div>
                    <div className="sm:text-right">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Receipt Date</p>
                        <p className="mt-2 font-semibold">{formatDate(receiptDate)}</p>
                        <p className="text-sm text-gray-600">Status: {campaign.status.replace('_', ' ').toUpperCase()}</p>
                    </div>
                </div>

                <div className="px-6 pb-6">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                                <tr>
                                    <th className="px-4 py-3">Description</th>
                                    <th className="px-4 py-3 text-right">Qty</th>
                                    <th className="px-4 py-3 text-right">Rate</th>
                                    <th className="px-4 py-3 text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                <tr>
                                    <td className="px-4 py-3">
                                        <p className="font-medium">{campaign.name}</p>
                                        <p className="text-xs text-gray-500">SMS campaign delivery</p>
                                    </td>
                                    <td className="px-4 py-3 text-right">{formatNumber(billedMessages)}</td>
                                    <td className="px-4 py-3 text-right">{formatMoney(currency, campaign.amount_per_sms || 0)}</td>
                                    <td className="px-4 py-3 text-right font-medium">{formatMoney(currency, smsCharge)}</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-3 font-medium" colSpan={3}>Workmanship fee</td>
                                    <td className="px-4 py-3 text-right font-medium">{formatMoney(currency, campaign.workmanship_fee || 0)}</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-3 font-medium" colSpan={3}>Sender ID setup</td>
                                    <td className="px-4 py-3 text-right font-medium">{formatMoney(currency, campaign.sender_id_price || 0)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <div className="w-full max-w-sm rounded-lg bg-gray-50 p-4">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Subtotal</span>
                                <span>{formatMoney(currency, totalDue)}</span>
                            </div>
                            <div className="mt-3 flex justify-between border-t border-gray-200 pt-3 text-lg font-bold">
                                <span>Total Due</span>
                                <span>{formatMoney(currency, totalDue)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid gap-3 text-sm text-gray-600 sm:grid-cols-3">
                        <div>
                            <span className="font-medium text-gray-900">Sender ID</span>
                            <p>{campaign.sender_id}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-900">Delivered</span>
                            <p>{formatNumber(campaign.total_delivered)}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-900">Failed</span>
                            <p>{formatNumber(campaign.total_failed)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignReceipt;
